import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Globe, PencilSimple, Trash, X as XIcon } from "@phosphor-icons/react";

const CURRENCIES = ["USD", "EUR", "GBP", "INR"];
const empty = {
  name: "", currency: "USD", countries: [],
  price_tiers: [], bonus_threshold: 10, bonus_quantity: 1,
  active: true, notes: "",
};

export default function PricingZones() {
  const [zones, setZones] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const r = await api.get("/pricing-zones");
    setZones(r.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setOpen(true); };
  const openEdit = (z) => { setEditing(z); setOpen(true); };

  const remove = async (z) => {
    if (!window.confirm(`Delete "${z.name}"?\n\nOrders already priced under this zone stay intact.`)) return;
    try {
      await api.delete(`/pricing-zones/${z.zone_id}`);
      toast.success("Zone deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="p-8" data-testid="zones-page">
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-1">Pricing</p>
          <h1 className="text-3xl font-black tracking-tighter">Pricing Zones</h1>
          <p className="text-sm text-zinc-500 mt-1">Group customer countries under a shared price ladder + currency. Orders auto-pick the correct zone from the customer country.</p>
        </div>
        <Button onClick={openNew} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-md" data-testid="new-zone-button">
          <Plus size={14} weight="bold" className="mr-1" /> New Zone
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {zones.map((z) => (
          <div key={z.zone_id} className="border border-zinc-200 bg-white p-5" data-testid={`zone-${z.zone_id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="flex items-center gap-2">
                  <Globe size={16} weight="bold" />
                  <h3 className="text-lg font-black tracking-tight">{z.name}</h3>
                </div>
                <p className="text-xs text-zinc-500 mt-1">Currency: <span className="font-mono font-semibold">{z.currency}</span> · Bonus: {z.bonus_threshold}→+{z.bonus_quantity}</p>
              </div>
              <div className="flex gap-1">
                <button onClick={() => openEdit(z)} className="p-1.5 hover:bg-zinc-100 rounded" data-testid={`edit-zone-${z.zone_id}`}><PencilSimple size={14} /></button>
                <button onClick={() => remove(z)} className="p-1.5 hover:bg-red-50 text-red-600 rounded" data-testid={`delete-zone-${z.zone_id}`}><Trash size={14} /></button>
              </div>
            </div>
            <div className="mb-3">
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Countries ({z.countries.length})</p>
              <div className="flex flex-wrap gap-1">
                {z.countries.slice(0, 12).map((c) => (
                  <span key={c} className="text-[10px] uppercase tracking-widest bg-zinc-100 text-zinc-700 px-1.5 py-0.5 border border-zinc-200">{c}</span>
                ))}
                {z.countries.length > 12 && <span className="text-[10px] text-zinc-500">+{z.countries.length - 12} more</span>}
              </div>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-1">Price ladder ({z.price_tiers.length} tiers)</p>
              {z.price_tiers.length === 0 ? (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1">No tiers yet — orders in this zone will fall back to the product&apos;s default ladder.</p>
              ) : (
                <div className="grid grid-cols-3 gap-1 text-[11px] font-mono">
                  {z.price_tiers.slice(0, 12).map((t) => (
                    <div key={t.min_qty} className="border border-zinc-100 px-2 py-1 bg-zinc-50">
                      <span className="text-zinc-500">{t.min_qty}×</span> {t.total_price ? `${z.currency} ${t.total_price}` : `${z.currency} ${(t.unit_price * t.min_qty).toFixed(2)}`}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}
        {zones.length === 0 && (
          <p className="text-sm text-zinc-500 col-span-2 text-center py-16">No pricing zones yet. Create one to start segmenting customer pricing by country.</p>
        )}
      </div>

      <ZoneDialog open={open} onOpenChange={setOpen} zone={editing} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}

function ZoneDialog({ open, onOpenChange, zone, onSaved }) {
  const [form, setForm] = useState(empty);
  const [countryInput, setCountryInput] = useState("");

  useEffect(() => {
    if (zone) setForm({ ...empty, ...zone });
    else setForm(empty);
    setCountryInput("");
  }, [zone, open]);

  const addCountry = () => {
    const c = countryInput.trim();
    if (!c || form.countries.includes(c)) return;
    setForm((f) => ({ ...f, countries: [...f.countries, c] }));
    setCountryInput("");
  };
  const removeCountry = (c) => setForm((f) => ({ ...f, countries: f.countries.filter((x) => x !== c) }));

  const setTier = (idx, key, value) => {
    setForm((f) => ({
      ...f,
      price_tiers: f.price_tiers.map((t, i) => (i === idx ? { ...t, [key]: value === "" ? undefined : parseFloat(value) } : t)),
    }));
  };
  const addTier = () => setForm((f) => ({ ...f, price_tiers: [...f.price_tiers, { min_qty: (f.price_tiers.length + 1), unit_price: 0 }] }));
  const removeTier = (idx) => setForm((f) => ({ ...f, price_tiers: f.price_tiers.filter((_, i) => i !== idx) }));

  const save = async () => {
    try {
      const payload = {
        ...form,
        price_tiers: form.price_tiers
          .filter((t) => t.min_qty && (t.unit_price || t.total_price))
          .map((t) => ({
            min_qty: parseInt(t.min_qty, 10),
            unit_price: parseFloat(t.unit_price) || (t.total_price ? parseFloat(t.total_price) / parseInt(t.min_qty, 10) : 0),
            ...(t.total_price ? { total_price: parseFloat(t.total_price) } : {}),
          }))
          .sort((a, b) => a.min_qty - b.min_qty),
      };
      if (zone) await api.put(`/pricing-zones/${zone.zone_id}`, payload);
      else await api.post("/pricing-zones", payload);
      toast.success(zone ? "Zone updated" : "Zone created");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="zone-dialog">
        <DialogHeader>
          <DialogTitle>{zone ? `Edit ${zone.name}` : "New Pricing Zone"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-3">
          <div className="grid grid-cols-2 gap-3">
            <F label="Zone name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Zone 1 — USA" data-testid="zone-name" /></F>
            <F label="Currency">
              <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                <SelectTrigger data-testid="zone-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </F>
          </div>
          <F label="Countries (Enter to add — write each country name as it appears on orders)">
            <div className="flex gap-2">
              <Input
                value={countryInput}
                onChange={(e) => setCountryInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCountry(); } }}
                placeholder="e.g. Portugal"
                data-testid="zone-country-input"
              />
              <Button type="button" onClick={addCountry} variant="outline" className="border-zinc-300">Add</Button>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              {form.countries.map((c) => (
                <span key={c} className="flex items-center gap-1 text-[10px] uppercase tracking-widest bg-zinc-100 text-zinc-700 px-2 py-1 border border-zinc-200">
                  {c}
                  <button type="button" onClick={() => removeCountry(c)} className="hover:text-red-600"><XIcon size={10} /></button>
                </span>
              ))}
            </div>
          </F>
          <div className="grid grid-cols-2 gap-3">
            <F label="Bonus threshold (buy N)"><Input type="number" min="0" value={form.bonus_threshold} onChange={(e) => setForm({ ...form, bonus_threshold: parseInt(e.target.value, 10) || 0 })} data-testid="zone-bonus-threshold" /></F>
            <F label="Bonus quantity (get M free)"><Input type="number" min="0" value={form.bonus_quantity} onChange={(e) => setForm({ ...form, bonus_quantity: parseInt(e.target.value, 10) || 0 })} data-testid="zone-bonus-quantity" /></F>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Price ladder (1-10 bags typically)</Label>
              <Button type="button" onClick={addTier} variant="outline" size="sm" className="border-zinc-300 text-xs" data-testid="zone-add-tier">+ Add tier</Button>
            </div>
            <div className="space-y-1" data-testid="zone-tiers">
              {form.price_tiers.map((t, i) => (
                <div key={i} className="grid grid-cols-[80px_1fr_1fr_40px] gap-2 items-center">
                  <Input type="number" min="1" value={t.min_qty || ""} onChange={(e) => setTier(i, "min_qty", e.target.value)} placeholder="Qty" className="text-center" />
                  <Input type="number" step="0.01" value={t.unit_price || ""} onChange={(e) => setTier(i, "unit_price", e.target.value)} placeholder="Unit price" />
                  <Input type="number" step="0.01" value={t.total_price || ""} onChange={(e) => setTier(i, "total_price", e.target.value)} placeholder="Total (optional, exact)" />
                  <button type="button" onClick={() => removeTier(i)} className="text-zinc-400 hover:text-red-600"><XIcon size={14} /></button>
                </div>
              ))}
              {form.price_tiers.length === 0 && <p className="text-xs text-zinc-500 italic">No tiers yet — click &quot;+ Add tier&quot;</p>}
            </div>
            <p className="text-[10px] text-zinc-500 mt-2">💡 Use <strong>Total</strong> for exact ladder prices (e.g. 3 bags = $800 exactly, not $800.01). Leave blank to compute Total = Unit × Qty.</p>
          </div>

          <F label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="zone-notes" /></F>

          <div className="flex items-center gap-2">
            <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="zone-active" />
            <Label className="text-sm">Active (used to price incoming orders)</Label>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="button" onClick={save} className="bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="zone-save">{zone ? "Save" : "Create Zone"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
